import React, { useState, useEffect } from "react";
import { Share2, Download } from "lucide-react";
import {
  useOutletContext,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config/api";

export default function CertificatePage() {
  const { userData } = useOutletContext() || {};
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const certificateId = searchParams.get("certificate_id");

  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 🔥 Helper Functions
  const normalizeUserData = (userData) => {
    return userData?.user || userData || {};
  };

  const extractUserName = (normalizedUser) => {
    return (
      normalizedUser.full_name ||
      normalizedUser.name ||
      (normalizedUser.first_name && normalizedUser.last_name
        ? `${normalizedUser.first_name} ${normalizedUser.last_name}`
        : null) ||
      normalizedUser.username ||
      normalizedUser.fullName ||
      "User"
    );
  };

  const getUserInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const getUserId = (normalizedUser) => {
    return normalizedUser.user_id || normalizedUser.id || "N/A";
  };

  const getAvatarUrl = (normalizedUser) => {
    return normalizedUser.avatar_url || null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
    });
  };

  // Normalize user data
  const normalizedUser = normalizeUserData(userData);
  const userName = extractUserName(normalizedUser);
  const nameToUse = userName;
  const userInitials = getUserInitials(nameToUse);
  const userId = getUserId(normalizedUser);
  const avatarUrl = getAvatarUrl(normalizedUser);
  const hasAvatar = Boolean(avatarUrl);

  const handleImageError = (e) => {
    e.target.onerror = null;
    e.target.style.display = "none";
  };

  // Fetch certificate details
  useEffect(() => {
    if (!certificateId) {
      setError("No certificate ID provided");
      setLoading(false);
      return;
    }

    const fetchCertificate = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("access_token");
        const headers = {
          Authorization: `Bearer ${token}`,
        };

        // Fetch all certificates and find the specific one
        const response = await axios.get(
          `${API_URL}/certificates/my-certificates`,
          { headers },
        );

        const foundCertificate = response.data.find(
          (cert) => cert.certificate_id === parseInt(certificateId),
        );

        if (!foundCertificate) {
          setError("Certificate not found");
        } else {
          setCertificate(foundCertificate);
        }
      } catch (err) {
        console.error("Error fetching certificate:", err);
        setError(
          err.response?.data?.detail ||
            "Failed to load certificate. Please try again later.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCertificate();
  }, [certificateId]);

  const handleShareCertificate = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${certificate?.title || "Course"} Certificate`,
          text: `Check out my certificate for ${certificate?.title || "this course"} from SkillsMD!`,
          url: window.location.href,
        });
      } else {
        // Fallback: Copy to clipboard
        await navigator.clipboard.writeText(window.location.href);
        alert("Certificate link copied to clipboard!");
      }
    } catch (error) {
      console.error("Error sharing certificate:", error);
    }
  };

  const handleDownloadCertificate = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      const response = await axios.get(
        `${API_URL}/certificates/${certificateId}/download`,
        {
          headers,
          responseType: "blob",
        },
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `certificate-${certificate?.title || "download"}.png`, // backend detects file type
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading certificate:", error);
      alert("Failed to download certificate. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading certificate...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate("/accomplishment")}
            className="px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700 transition-colors"
          >
            Back to Accomplishments
          </button>
        </div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Certificate not found</p>
          <button
            onClick={() => navigate("/accomplishment")}
            className="px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700 transition-colors"
          >
            Back to Accomplishments
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Certificate Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-8 h-[424px] flex flex-col items-start">
            <h3 className="text-stone-900 text-xl font-bold font-['Roboto'] leading-8 mb-4">
              Congratulations!
            </h3>

            <p className="flex items-start text-neutral-700 text-lg font-normal font-['Roboto'] leading-8">
              SkillsMD confirms the successful completion of the{" "}
              {certificate.title} course by {nameToUse}
            </p>

            <div className="flex items-start text-neutral-700 text-base font-normal font-['Roboto'] leading-8">
              Grade Achieved:{" "}
              <span className="text-stone-900 font-bold">
                {certificate.grade ? `${certificate.grade}%` : "N/A"}
              </span>
            </div>

            {/* <div className="flex items-start text-neutral-700 text-base font-normal font-['Roboto'] leading-8 mt-2">
              Certificate ID:{" "}
              <span className="text-stone-900 font-bold">
                {certificate.serial_number || certificate.certificate_id}
              </span>
            </div> */}

            <div className="flex flex-col gap-5 mt-auto">
              <button
                onClick={handleShareCertificate}
                className="w-full px-6 py-4 bg-[#4AACC5] rounded-md flex justify-center items-center gap-2.5 border border-[#4AACC5] hover:bg-[#3a99b0] transition-colors"
              >
                <Share2 className="w-3.5 h-3.5 text-white" />
                <span className="text-white text-base font-semibold font-['Roboto']">
                  Share Certificate
                </span>
              </button>

              <button
                onClick={handleDownloadCertificate}
                className="w-full px-6 py-4 rounded-md border border-[#4AACC5] flex justify-center items-center gap-2.5 hover:bg-teal-50 transition-colors"
              >
                <Download className="w-4 h-4 text-[#4AACC5]" />
                <span className="text-[#4AACC5] text-base font-semibold font-['Roboto']">
                  Download Certificate
                </span>
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-2 p-8 h-[424px] flex justify-center items-center">
            {certificate.file_url ? (
              <div className="w-full h-full flex justify-center items-center">
                <img
                  src={certificate.file_url}
                  alt="Certificate Preview"
                  onError={handleImageError}
                />
              </div>
            ) : (
              <div className="text-gray-500 text-center">
                Certificate preview not available
              </div>
            )}
          </div>
        </div>

        {/* User Profile Card */}
        <div className="w-full h-56 relative">
          <div className="w-full h-56 left-0 top-0 absolute bg-white rounded-lg border border-gray-200"></div>
          <div className="left-[23px] top-[131px] absolute justify-start">
            <span className="text-neutral-700 text-xl font-normal font-['Roboto'] leading-8">
              Your name, {nameToUse}, is verified. This is the name that will
              appear on your certificates.
              {/* If you have questions or <br />
              need help with your ID Verification, visit our{" "} */}
            </span>
            {/* <span className="text-cyan-600 text-xl font-normal font-['Roboto'] underline leading-8">
              ID Verification support page
            </span>
            <span className="text-neutral-700 text-xl font-normal font-['Roboto'] leading-8">
              .
            </span> */}
          </div>
          <div className="left-[24.28px] top-[24.28px] absolute inline-flex justify-start items-center gap-6">
            {hasAvatar ? (
              <img
                className="w-20 h-20 rounded"
                src={avatarUrl}
                alt="avatar"
                onError={(e) => {
                  console.warn(
                    "Avatar failed to load, showing initials instead",
                  );
                  e.target.onerror = null; // prevent loop
                  e.target.style.display = "none"; // hide broken image
                }}
              />
            ) : (
              <div className="w-20 h-20 rounded bg-[#4AACC5] flex items-center justify-center text-xl font-bold text-white">
                {userInitials}
              </div>
            )}

            <div className="w-64 inline-flex flex-col justify-start items-start gap-3">
              <div className="px-1.5 py-px bg-[#4AACC5] rounded-lg inline-flex justify-center items-center gap-2.5">
                <div className="justify-start text-white text-sm font-normal font-['Roboto'] leading-5">
                  ID #{userId}
                </div>
              </div>
              <div className="flex items-start self-stretch justify-start text-zinc-800 text-xl font-bold font-['Roboto']">
                {nameToUse}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
